import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { RefreshCw, AlertCircle, CheckCircle, Clock, XCircle, Play, Trash2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EnrichmentQueueManager = () => {
  const [queueStatus, setQueueStatus] = useState(null);
  const [failedLeads, setFailedLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [retrying, setRetrying] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [fixing, setFixing] = useState(false);
  const [retryingZeroScores, setRetryingZeroScores] = useState(false);
  const [cleaningOrphaned, setCleaningOrphaned] = useState(false);
  const [resettingFake, setResettingFake] = useState(false);
  const [resettingStuck, setResettingStuck] = useState(false);

  useEffect(() => {
    fetchQueueStatus();
    fetchFailedLeads();
    
    // Auto-refresh every 10 seconds
    const interval = setInterval(() => {
      fetchQueueStatus();
      fetchFailedLeads();
    }, 10000);
    
    return () => clearInterval(interval);
  }, []);

  const fetchQueueStatus = async () => {
    try {
      const response = await axios.get(`${API}/admin/enrichment-queue/status`, {
        withCredentials: true
      });
      setQueueStatus(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Failed to fetch queue status:', error);
      setLoading(false);
    }
  };

  const fetchFailedLeads = async () => {
    try {
      const response = await axios.get(`${API}/admin/enrichment-queue/failed-leads`, {
        withCredentials: true
      });
      setFailedLeads(response.data.failed_leads || []);
    } catch (error) {
      console.error('Failed to fetch failed leads:', error);
    }
  };

  const handleRetryFailed = async () => {
    setRetrying(true);
    try {
      const response = await axios.post(
        `${API}/admin/enrichment-queue/retry-failed`,
        {},
        { withCredentials: true }
      );
      
      toast.success(`Retrying ${response.data.retried} failed enrichments`);
      
      // Refresh data
      setTimeout(() => {
        fetchQueueStatus();
        fetchFailedLeads();
      }, 1000);
    } catch (error) {
      toast.error('Failed to retry enrichments');
    } finally {
      setRetrying(false);
    }
  };

  const handleSyncQueue = async () => {
    setSyncing(true);
    try {
      const response = await axios.post(
        `${API}/admin/enrichment-queue/sync-from-leads`,
        {},
        { withCredentials: true }
      );
      
      toast.success(response.data.message);
      
      // Refresh data
      setTimeout(() => {
        fetchQueueStatus();
      }, 1000);
    } catch (error) {
      toast.error('Failed to sync queue');
    } finally {
      setSyncing(false);
    }
  };

  const handleFixEnrichedStatus = async () => {
    setFixing(true);
    try {
      const response = await axios.post(
        `${API}/admin/enrichment-queue/fix-enriched-status`,
        {},
        { withCredentials: true }
      );
      
      toast.success(response.data.message);
      
      // Refresh data
      setTimeout(() => {
        fetchQueueStatus();
      }, 1000);
    } catch (error) {
      toast.error('Failed to fix enriched status');
    } finally {
      setFixing(false);
    }
  };

  const handleRetryZeroScores = async () => {
    setRetryingZeroScores(true);
    try {
      const response = await axios.post(
        `${API}/admin/enrichment-queue/retry-zero-scores`,
        {},
        { withCredentials: true }
      );
      
      toast.success(response.data.message);
      
      // Refresh data
      setTimeout(() => {
        fetchQueueStatus();
      }, 1000);
    } catch (error) {
      toast.error('Failed to retry zero-score leads');
    } finally {
      setRetryingZeroScores(false);
    }
  };

  const handleCleanOrphaned = async () => {
    setCleaningOrphaned(true);
    try {
      const response = await axios.post(
        `${API}/admin/enrichment-queue/clean-orphaned`,
        {},
        { withCredentials: true }
      );
      
      toast.success(response.data.message);
      
      // Refresh data
      setTimeout(() => {
        fetchQueueStatus();
      }, 1000);
    } catch (error) {
      toast.error('Failed to clean orphaned queue entries');
    } finally {
      setCleaningOrphaned(false);
    }
  };

  const handleResetFakeEnriched = async () => {
    setResettingFake(true);
    try {
      const response = await axios.post(
        `${API}/admin/enrichment-queue/reset-fake-enriched`,
        {},
        { withCredentials: true }
      );
      
      toast.success(response.data.message);
      
      // Refresh data
      setTimeout(() => {
        fetchQueueStatus();
      }, 1000);
    } catch (error) {
      toast.error('Failed to reset fake enriched leads');
    } finally {
      setResettingFake(false);
    }
  };

  const handleResetStuckProcessing = async () => {
    setResettingStuck(true);
    try {
      const response = await axios.post(
        `${API}/admin/enrichment-queue/reset-stuck-processing`,
        {},
        { withCredentials: true }
      );
      
      toast.success(response.data.message);
      
      // Refresh data
      setTimeout(() => {
        fetchQueueStatus();
      }, 1000);
    } catch (error) {
      toast.error('Failed to reset stuck processing leads');
    } finally {
      setResettingStuck(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <RefreshCw className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Rate Limit Warning Banner */}
      <Card className="border-orange-200 bg-orange-50">
        <CardHeader>
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-orange-600 flex-shrink-0 mt-0.5" />
            <div>
              <CardTitle className="text-orange-900">Enrichment Rate Limiting Active</CardTitle>
              <CardDescription className="text-orange-700 mt-2">
                To prevent OpenAI API rate limit errors, enrichments are processed in a queue with automatic rate limiting.
                <br />
                <strong className="font-semibold">Current Limit: 5 leads per minute</strong> (1 lead every 12 seconds)
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-2 text-sm text-orange-800">
            <p>• <strong>Bulk imports:</strong> All leads are automatically queued for gradual enrichment</p>
            <p>• <strong>Manual enrichment:</strong> Individual enrichment requests are added to the queue with high priority</p>
            <p>• <strong>Auto-retry:</strong> Failed enrichments are automatically retried up to 3 times with exponential backoff</p>
            <p>• <strong>OpenAI Tier:</strong> Your account has a 30,000 tokens/minute limit (~10 enrichments/minute max capacity)</p>
          </div>
        </CardContent>
      </Card>

      {/* Queue Status Dashboard */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-slate-600">Queue Size</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <span className="text-3xl font-bold text-slate-900">{queueStatus?.queue_size || 0}</span>
              <Clock className="w-8 h-8 text-blue-500" />
            </div>
            <p className="text-xs text-slate-500 mt-2">
              Est: {queueStatus?.estimated_completion_minutes?.toFixed(1) || 0} min
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-slate-600">Processing</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <span className="text-3xl font-bold text-blue-600">{queueStatus?.processing || 0}</span>
              <RefreshCw className={`w-8 h-8 text-blue-500 ${queueStatus?.is_processing ? 'animate-spin' : ''}`} />
            </div>
            <p className="text-xs text-slate-500 mt-2">
              {queueStatus?.is_processing ? 'Queue active' : 'Queue idle'}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-slate-600">Completed</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <span className="text-3xl font-bold text-green-600">{queueStatus?.completed || 0}</span>
              <CheckCircle className="w-8 h-8 text-green-500" />
            </div>
            <p className="text-xs text-slate-500 mt-2">Successfully enriched</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-slate-600">Failed</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <span className="text-3xl font-bold text-red-600">{queueStatus?.failed || 0}</span>
              <XCircle className="w-8 h-8 text-red-500" />
            </div>
            <p className="text-xs text-slate-500 mt-2">Exceeded max retries</p>
          </CardContent>
        </Card>
      </div>

      {/* Rate Limit Info */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Rate Limit Configuration</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-slate-600">Leads per Minute</p>
              <p className="text-2xl font-semibold text-slate-900">{queueStatus?.rate_limit_per_minute || 5}</p>
            </div>
            <div>
              <p className="text-sm text-slate-600">Delay Between Leads</p>
              <p className="text-2xl font-semibold text-slate-900">12 seconds</p>
            </div>
            <div>
              <p className="text-sm text-slate-600">Max Retries</p>
              <p className="text-2xl font-semibold text-slate-900">3 attempts</p>
            </div>
            <div>
              <p className="text-sm text-slate-600">Pending in Queue</p>
              <p className="text-2xl font-semibold text-slate-900">{queueStatus?.pending || 0}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Sync Queue Button - for fixing mismatched queue status */}
      {queueStatus && queueStatus.queue_size === 0 && (
        <Card className="border-blue-200 bg-blue-50">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm">
                  <p className="font-medium text-blue-900">Queue appears empty but leads might be stuck</p>
                  <p className="text-blue-700 mt-1">
                    If you see leads with "queued" status that aren't processing, click Sync to fix the queue.
                  </p>
                </div>
              </div>
              <Button
                onClick={handleSyncQueue}
                disabled={syncing}
                variant="outline"
                size="sm"
                className="border-blue-300 text-blue-700 hover:bg-blue-100"
              >
                {syncing ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Syncing...
                  </>
                ) : (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Sync Queue
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Fix Enriched Status Button - for when completed enrichments don't show on leads page */}
      {queueStatus && queueStatus.completed > 0 && (
        <Card className="border-amber-200 bg-amber-50">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm">
                  <p className="font-medium text-amber-900">Enrichment Status Mismatch Detected</p>
                  <p className="text-amber-700 mt-1">
                    {queueStatus.completed} enrichments completed here, but leads table may show 0 enriched. Click Fix to synchronize the status.
                  </p>
                </div>
              </div>
              <Button
                onClick={handleFixEnrichedStatus}
                disabled={fixing}
                variant="outline"
                size="sm"
                className="border-amber-300 text-amber-700 hover:bg-amber-100"
              >
                {fixing ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Fixing...
                  </>
                ) : (
                  <>
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Fix Status
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Reset Fake Enriched Leads Button */}
      <Card className="border-red-200 bg-red-50">
        <CardContent className="pt-6">
          <div className="flex items-center justify-between">
            <div className="flex items-start gap-3">
              <XCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm">
                <p className="font-medium text-red-900">Reset Fake "Enriched" Leads</p>
                <p className="text-red-700 mt-1">
                  Some leads show as "enriched" but have empty data and score 0. Click to reset them to queued status for proper enrichment.
                </p>
              </div>
            </div>
            <Button
              onClick={handleResetFakeEnriched}
              disabled={resettingFake}
              variant="outline"
              size="sm"
              className="border-red-300 text-red-700 hover:bg-red-100"
            >
              {resettingFake ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Resetting...
                </>
              ) : (
                <>
                  <XCircle className="w-4 h-4 mr-2" />
                  Reset Fake Enriched
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Reset Stuck Processing Leads */}
      {queueStatus && queueStatus.processing > 0 && (
        <Card className="border-yellow-200 bg-yellow-50">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div className="flex items-start gap-3">
                <Clock className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm">
                  <p className="font-medium text-yellow-900">Reset Stuck Processing Leads</p>
                  <p className="text-yellow-700 mt-1">
                    {queueStatus.processing} lead(s) stuck in "processing" status. These may have timed out or encountered errors. Click to reset and retry.
                  </p>
                </div>
              </div>
              <Button
                onClick={handleResetStuckProcessing}
                disabled={resettingStuck}
                variant="outline"
                size="sm"
                className="border-yellow-300 text-yellow-700 hover:bg-yellow-100"
              >
                {resettingStuck ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Resetting...
                  </>
                ) : (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Reset Stuck
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Clean Orphaned Queue Entries */}
      {queueStatus && queueStatus.queue_size > 10 && (
        <Card className="border-purple-200 bg-purple-50">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm">
                  <p className="font-medium text-purple-900">Clean Orphaned Queue Entries</p>
                  <p className="text-purple-700 mt-1">
                    Queue shows {queueStatus.queue_size} items but you may have fewer leads. Click to remove queue entries for deleted leads.
                  </p>
                </div>
              </div>
              <Button
                onClick={handleCleanOrphaned}
                disabled={cleaningOrphaned}
                variant="outline"
                size="sm"
                className="border-purple-300 text-purple-700 hover:bg-purple-100"
              >
                {cleaningOrphaned ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Cleaning...
                  </>
                ) : (
                  <>
                    <Trash2 className="w-4 h-4 mr-2" />
                    Clean Queue
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Failed Enrichments */}
      {failedLeads.length > 0 && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-lg">Failed Enrichments</CardTitle>
                <CardDescription className="mt-1">
                  These leads failed enrichment after 3 retry attempts
                </CardDescription>
              </div>
              <Button
                onClick={handleRetryFailed}
                disabled={retrying}
                variant="outline"
                size="sm"
              >
                {retrying ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Retrying...
                  </>
                ) : (
                  <>
                    <Play className="w-4 h-4 mr-2" />
                    Retry All Failed
                  </>
                )}
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Lead</TableHead>
                  <TableHead>Company</TableHead>
                  <TableHead>Error Message</TableHead>
                  <TableHead>Retry Count</TableHead>
                  <TableHead>Failed At</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {failedLeads.map((lead) => (
                  <TableRow key={lead.lead_id}>
                    <TableCell className="font-medium">{lead.lead_name}</TableCell>
                    <TableCell>{lead.company}</TableCell>
                    <TableCell className="max-w-md truncate text-xs text-red-600">
                      {lead.error_message}
                    </TableCell>
                    <TableCell>
                      <Badge variant="destructive">{lead.retry_count}/3</Badge>
                    </TableCell>
                    <TableCell className="text-xs text-slate-500">
                      {new Date(lead.failed_at).toLocaleString()}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default EnrichmentQueueManager;
